import type { KidMarker } from '../../../../../../../api/kids/types';
import type {
  PdfElement,
  SetTableTag,
} from '../../../types/KidPdfGeneratorV2.types';
import { parseContent } from '../../../utils/kidDataTransformers';
import {
  buildCompositionOfCostsTableFromMarkers,
  buildCostsOverTimeTableFromMarkers,
  buildPerformanceScenariosTableFromMarkers,
  buildSriValueFromMarkers,
} from './buildTablesFromMarkers';
import { getMarkersForPresetTag } from './getMarkersForPresetTag';
import { mergeInjectedPresetTableContent } from './presetInjectedCells';

const SET_TABLE_BUILDERS: Record<
  SetTableTag,
  (markers: KidMarker[]) => string[][]
> = {
  composition_of_costs_table: buildCompositionOfCostsTableFromMarkers,
  costs_over_time_table: buildCostsOverTimeTableFromMarkers,
  performance_scenarios_table: buildPerformanceScenariosTableFromMarkers,
};

function parseExistingTableContent(content: unknown): string[][] | undefined {
  const parsed = parseContent(content as string);
  if (!Array.isArray(parsed) || parsed.length === 0) return undefined;
  if (!Array.isArray(parsed[0])) return undefined;
  return parsed as string[][];
}

export type InjectPresetFiguresResult = {
  content: PdfElement['content'];
  existing_markers: KidMarker[];
};

/**
 * Seed a preset KID Figures block from markers (latest calc values live in markers).
 * Injected cells update from markers; editable cells are preserved.
 */
export function buildInjectedPresetElementUpdate(
  tag: string,
  existingContent: unknown,
  markers: KidMarker[],
): InjectPresetFiguresResult | null {
  if (tag === 'sri_chart' || tag === 'sri') {
    const sriValue = buildSriValueFromMarkers(markers);
    if (sriValue == null) return null;
    return {
      content: sriValue,
      existing_markers: getMarkersForPresetTag('sri_chart', markers),
    };
  }

  if (!(tag in SET_TABLE_BUILDERS)) return null;

  const setTag = tag as SetTableTag;
  const built = SET_TABLE_BUILDERS[setTag](markers);
  if (built.length === 0) return null;

  return {
    content: mergeInjectedPresetTableContent(
      setTag,
      built,
      parseExistingTableContent(existingContent),
    ),
    existing_markers: getMarkersForPresetTag(setTag, markers),
  };
}
